import { getItemState } from "@ui/components/shared/getItemState";
import { type TileRenderState, type TileVariants, tile, tileGroupLayout } from "@ui/components/Tile/styles";
import { type Accent, accentScope } from "@ui/lib/accent-scope";
import { composeRenderProps } from "@ui/primitives/shared";
import { type ComponentType, createContext, type ReactNode, use } from "react";

type TileAlign = "vertical" | "horizontal";

interface TileSelectionGroupContextValue {
  color: NonNullable<TileVariants["color"]>;
  control: TileVariants["control"] | null;
  hierarchy: NonNullable<TileVariants["hierarchy"]>;
  shape: NonNullable<TileVariants["shape"]>;
  size: TileVariants["size"];
  align: TileAlign;
}

/** The tile variant props both group flavours layer on top of their RAC group props. */
export interface TileSelectionGroupVariantProps {
  color?: TileVariants["color"];
  control?: TileVariants["control"] | null;
  hierarchy?: TileVariants["hierarchy"];
  shape?: TileVariants["shape"];
  size?: TileVariants["size"];
  align?: TileAlign;
}

export type TileSelectionGroupProps<GroupProps> = GroupProps & TileSelectionGroupVariantProps & { children: ReactNode };

export interface TileSelectionItemProps {
  children?: ReactNode | ((state: TileRenderState) => ReactNode);
  className?: string;
  description?: string;
  isDisabled?: boolean;
  label?: string;
  /** Replaces the default label slot while keeping the tile chrome and indicator
   *  (e.g. RadioGroupWithFollowUp swaps the label for an inline input when selected). */
  renderLabel?: (state: TileRenderState, labelClassName: string) => ReactNode;
  value: string;
}

/** Structural subset of the RAC Checkbox/Radio props the factory drives. */
interface SelectionItemComponentProps {
  "data-accent"?: Accent;
  value: string;
  isDisabled?: boolean;
  className?: (state: TileRenderState) => string;
  children?: (state: TileRenderState) => ReactNode;
}

interface TileSelectionGroupConfig<GroupProps> {
  /** RAC group primitive (CheckboxGroup / RadioGroup). */
  Group: ComponentType<GroupProps>;
  /** RAC item primitive (Checkbox / Radio). */
  Item: ComponentType<SelectionItemComponentProps>;
  defaultControl: NonNullable<TileVariants["control"]>;
  /** Content rendered inside the indicator disc (e.g. the checkbox check glyph). */
  renderIndicatorContent?: (state: TileRenderState) => ReactNode;
}

const RAISED = "raised" as const;
const SELECTED = "selected" as const;

/**
 * Builds a Tile-styled selection group (root + item) from a RAC group/item
 * pair. TileCheckboxGroup and TileRadioGroup are thin wrappers over this —
 * the two only differ in the RAC pair, the default control, and the checkbox's
 * indicator glyph.
 */
export function createTileSelectionGroup<GroupProps extends { className?: unknown; children?: unknown }>(
  config: TileSelectionGroupConfig<GroupProps>,
) {
  const TileSelectionGroupContext = createContext<TileSelectionGroupContextValue>({
    color: "red",
    control: config.defaultControl,
    hierarchy: "flat",
    shape: "pill",
    size: "5xl",
    align: "vertical",
  });

  function TileSelectionGroupRoot({
    children,
    color = "red",
    control = config.defaultControl,
    hierarchy = "flat",
    shape = "pill",
    size = "5xl",
    align = "vertical",
    ...props
  }: TileSelectionGroupProps<GroupProps>) {
    // After destructuring the tile variant props, the rest is exactly the RAC
    // group props — TypeScript can't see through the generic subtraction.
    const groupProps = props as GroupProps;

    return (
      <TileSelectionGroupContext value={{ color, control, hierarchy, shape, size, align }}>
        <config.Group
          {...groupProps}
          className={
            composeRenderProps(
              groupProps.className as string | undefined,
              (className) => `${tileGroupLayout[align]}${className ? ` ${className}` : ""}`,
            ) as GroupProps["className"]
          }
        >
          {children}
        </config.Group>
      </TileSelectionGroupContext>
    );
  }

  function TileSelectionGroupItem({
    children,
    className,
    description,
    label,
    renderLabel,
    ...props
  }: TileSelectionItemProps) {
    const { color, control, hierarchy, shape, size, align } = use(TileSelectionGroupContext);
    const hasControl = control != null;

    // Without a control indicator, use raised hierarchy for selected state (inset border only,
    // no bg fill) while keeping the original hierarchy for default/hover.
    const resolveHierarchy = (state: string) => (!hasControl && state === SELECTED ? RAISED : hierarchy);

    return (
      <config.Item
        {...props}
        {...accentScope(color)}
        className={({ isDisabled, isSelected, isHovered, isFocusVisible }) => {
          const state = getItemState(isDisabled, isSelected, isHovered);
          const extraClasses = [!hasControl && "items-center gap-3", className].filter(Boolean).join(" ");

          return tile({
            hierarchy: resolveHierarchy(state),
            shape,
            size,
            align,
            color,
            control: control ?? undefined,
            state,
            isFocusVisible,
          }).root({ className: extraClasses || undefined });
        }}
      >
        {({ isDisabled, isSelected, isHovered, isFocusVisible }) => {
          const state = getItemState(isDisabled, isSelected, isHovered);
          const renderState: TileRenderState = { isDisabled, isFocusVisible, isHovered, isSelected };
          const styles = tile({
            hierarchy: resolveHierarchy(state),
            shape,
            size,
            color,
            control: control ?? undefined,
            state,
            isFocusVisible,
          });

          if (typeof children === "function") {
            return children(renderState);
          }

          if (hasControl) {
            return (
              <>
                <div aria-hidden="true" className={styles.indicator()}>
                  {config.renderIndicatorContent?.(renderState)}
                </div>
                {renderLabel ? (
                  renderLabel(renderState, styles.label())
                ) : (
                  <span className={styles.label()}>{children ?? label}</span>
                )}
              </>
            );
          }

          return (
            <>
              {children}
              {label && <span className={styles.label()}>{label}</span>}
              {description && <p className={styles.description()}>{description}</p>}
            </>
          );
        }}
      </config.Item>
    );
  }

  return Object.assign(TileSelectionGroupRoot, { Item: TileSelectionGroupItem });
}
